import { useEffect, useContext } from "react";
import {
  getIdTokenClaims,
  heapIdentifyFromIdToken,
  setHeapAccountProperties,
  setHeapEventProperties,
  setHeapUserProperties,
} from "@hoteltradergit/ht-telemetry-ui-lib";
import { PropertyStoreContext } from "../contexts/PropertyStoreContext";

/**
 * Derives user type from user role
 * All supplier portal users are categorized as "Supplier"
 * The specific role (REVENUE_MANAGER, PROPERTY_ADMIN, etc.) is captured in userRole property
 */
const getUserType = (userRole) => {
  if (!userRole) return null;
  return "Supplier";
};

/**
 * EO-1184: who is acting, for events that need hotel-vs-HT attribution
 * (promo_extended, and any autocaptured promo action). Supplier users are the
 * hotel; internal (AM/Supply) users act on the hotel's behalf.
 *
 * Set as an event property rather than passed per call site, so it rides every
 * event Heap captures — including data-heap-id clicks, which cannot carry
 * app-derived attributes of their own.
 *
 * Returns null when the token carries no userType claim, so an unknown actor is
 * absent from the data rather than silently labelled as one side or the other.
 */
const getActorType = (userType) => {
  if (!userType) return null;
  return userType === "SUPPLIER" ? "hotel" : "account_manager";
};

/**
 * Hook to manage Heap Analytics user identification and property tracking
 * - Identifies user once per session
 * - Updates event properties whenever selected property changes
 *
 * Heap calls go through @hoteltradergit/ht-telemetry-ui-lib's guarded
 * wrappers: they typeof-guard window.heap and never throw.
 */
const useHeapIdentify = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);

  // Phase 1: identify user once per session — the lib reads the ROCP id
  // token, extracts the email claim, dedupes repeats, and retries until the
  // identify call actually dispatches
  useEffect(() => {
    heapIdentifyFromIdToken();
  }, []);

  // Phase 2: Update event properties on every property change
  useEffect(() => {
    const claims = getIdTokenClaims();
    if (!claims) return;

    const userRole = claims["custom:userRole"];
    const userType = getUserType(userRole);

    const eventProperties = {};

    if (userRole) {
      eventProperties.userRole = userRole;
    }

    if (userType) {
      eventProperties.userType = userType;
    }

    const actorType = getActorType(claims["custom:userType"]);
    if (actorType) {
      eventProperties.actorType = actorType;
    }

    if (selectedProperty?.propertyName) {
      eventProperties.propertyName = selectedProperty.propertyName;
    }

    if (selectedProperty?.propertyId) {
      eventProperties.propertyId = selectedProperty.propertyId;
    }

    if (Object.keys(eventProperties).length > 0) {
      // setHeapEventProperties is clear-before-add: it resets Heap's
      // persisted event properties before re-adding the full set, so stale
      // keys/values from older sessions can't accumulate. Safe here: this
      // hook is the app's only event-properties writer.
      setHeapEventProperties(eventProperties);
    }

    // Org identity as USER properties. Heap's Account-Based Analytics
    // derives its Account ID from the accountId user property — the
    // mapping is configured in Heap once ABA is enabled on the plan, so
    // this must ship as user properties: the heap.js bundles served today
    // (classic and v5 core) implement no account API. accountId follows
    // the selected property (last-touch for multi-property users).
    if (selectedProperty?.propertyId) {
      const accountProperties = {
        accountId: `property:${selectedProperty.propertyId}`,
        accountType: "supplier_property",
      };

      if (selectedProperty?.propertyName) {
        accountProperties.accountName = selectedProperty.propertyName;
      }

      setHeapUserProperties(accountProperties);

      // Inert until Heap ships the accounts SDK plugin to this
      // environment (the guard lives in the lib); activates on its own
      // once the API exists.
      setHeapAccountProperties(accountProperties);
    }
  }, [selectedProperty]);
};

export default useHeapIdentify;
